import { Breadcrumbs } from '@components/BreadCrumbs/State/reducer'
import { useBreadCrumbsState } from '@components/BreadCrumbs/State/state'

import {
  CwsDivider,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsH2,
  CwsSection,
  CwsTab,
  CwsTabContent,
  CwsTabContentItem,
  CwsTabItem,
} from '@components/cws'
import { getFetcher } from '@components/pages/ProductPage'
import { ProductSlider, ProductViewDescription, ProductViewHeading, ProductViewParameters } from '@components/product'
import { getPromotionsTag } from '@components/product/ProductCard/ProductTagsRow/tags-utils'
import { useInitialData } from '@context/initialDataContext'
import { useAppState } from '@context/state'
import { useBundleLabel } from '@hooks/useBundleLabel'
import { useCurrency } from '@hooks/useCurrency'
import { usePriceInfo } from '@hooks/usePriceInfo'
import { useTranslate } from '@hooks/useTranslation'
import { UserData, viewItem } from '@lib/gtm'
import { KK_LANG_IDS, Locale } from '@model/locales'
import type { Product } from '@model/product'
import type { ProductReviewResult } from '@model/product/ProductReviewResult.js'
import type { RecommendationsResult } from '@model/product/ProductSearchResult'
import { SliderButton } from '@ui/components'
import ZoomImage from '@ui/components/ZoomImage/ZoomImage'
import { device1024, device360, device600, useResize } from '@ui/hooks/useResize'
import { isCampaignValid } from '@utils/campaign-utils'
import {
  getCustomAttrCustomFacetCampaign,
  getCustomAttrCustomFacetLabel,
} from '@utils/getCustomAttrCustomFacetCampaign'
import { getFullImageUrl } from '@utils/getFullImageUrl'
import { getProductTitle } from '@utils/getProductTitle'
import { getCustomerUserData } from '@utils/getUserData'
import { usePaginationLogic } from '@utils/pagination'
import { parseDescriptionWithBreadCrumbs } from '@utils/parseDescriptionWithBreadCrumbs'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import { extractSrcSetFromImagesNamesArray } from '@utils/product/image'
import { getFreeProductInfo } from '@utils/product/old-price-utils'
import { getSlug } from '@utils/slugs'
import clsx from 'clsx'
import Image from 'next/image'
import { useRouter } from 'next/router'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import StickyBox from 'react-sticky-box'
import useSWRImmutable from 'swr/immutable'
import { ProductViewDeliveryInfo } from '../ProductViewDeliveryInfo'
import { ProductViewReviews } from '../ProductViewReviews'
import moduleStyles from './ProductView.module.scss'

export interface ProductViewProps {
  product: Product
  reviews: ProductReviewResult
  recommendations: RecommendationsResult
  recentlyViewedProducts: Product[]
}

const ProductView: FC<ProductViewProps> = (props) => {
  const { product, reviews, recommendations, recentlyViewedProducts } = props
  const { deviceSize } = useResize()

  const {
    rating,
    sku,
    manufacturerName,
    image,
    description: descriptionJSON,
    numberReviews,
    images,
    customAttrArray,
    promotionResults,
    custom4: campaignValidityData,
  } = product
  const { query, locale } = useRouter()
  const { dispatch } = useBreadCrumbsState()
  const { description, breadcrumbs } = parseDescriptionWithBreadCrumbs(descriptionJSON)

  const { currency } = useCurrency()
  const [averageRating, setAverageRating] = useState<number>(rating)
  const [localReviews, setLocalReviews] = useState<number>(numberReviews)
  const {
    state: { customer },
  } = useAppState()
  const isGWPProduct = useMemo(() => {
    const { normalPrice = 0, topPrice = 0 } = product.priceInfo
    return sku.toLowerCase().includes('gwp') && normalPrice === 0 && topPrice === 0
  }, [sku, product])
  useEffect(() => {
    if (breadcrumbs) {
      const breadcrumbsArray: { id: number; translation: string }[] = breadcrumbs
      const breadcrumbsListWithLink = breadcrumbsArray.reduce((acc, val): Breadcrumbs[] => {
        const slug = getSlug(`${val.translation}`, val.id)
        let href = query.catalogId ? `/${locale}/${query.catalogId}/category/${slug}` : `/${locale}/category/${slug}`

        return [...acc, { href, title: val.translation }]
      }, [] as Breadcrumbs[])

      if (query.catalogId && catalogs.length) {
        const currentCatalog = catalogs.find((item) => item.id === query.catalogId)
        if (currentCatalog) {
          dispatch({
            type: 'setBreadcrumbs',
            payload: [
              { href: '/', title: translate('breadcrumb.home') },
              { href: `/${locale}/${query.catalogId}`, title: currentCatalog.title },
              ...breadcrumbsListWithLink,
            ],
          })
        } else {
          dispatch({
            type: 'setBreadcrumbs',
            payload: [{ href: '/', title: translate('breadcrumb.home') }, ...breadcrumbsListWithLink],
          })
        }
      }
    }
  }, [descriptionJSON, locale, query])
  const imagesSrcSet = useMemo(() => {
    const imagesNames = images?.imgNames ?? []

    return extractSrcSetFromImagesNamesArray(imagesNames)
  }, [images])

  const isDescriptionAvailable = useMemo(() => Boolean(description) && description?.trim(), [descriptionJSON])

  const extraImages: KeyValue[] = imagesSrcSet.map((imageItem) => ({
    key: imageItem.index,
    value: imageItem.src.medium,
  }))

  const hasExtraImages = extraImages && extraImages.length > 1

  const [currentImageIndex, setCurrentImageIndex] = useState(
    hasExtraImages ? extraImages.sort((a, b) => parseInt(a.key.toString()) - parseInt(b.key.toString()))[0].key : 0
  )

  const handleSelectImage = useCallback((image: KeyValue) => {
    setCurrentImageIndex(image.key)
  }, [])

  const selectedImageIndex = extraImages.map((i) => i.key).indexOf(currentImageIndex) ?? 0

  const handleClickNextImage = () => {
    const isSelectedLast = extraImages[extraImages.length - 1].key === currentImageIndex
    !isSelectedLast && handleSelectImage(extraImages[selectedImageIndex + 1])
  }

  const handleClickPrevImage = () => {
    const isSelectedFirst = extraImages[0].key === currentImageIndex
    !isSelectedFirst && handleSelectImage(extraImages[selectedImageIndex - 1])
  }

  const currentImage = imagesSrcSet?.find((img) => img.index === currentImageIndex)?.src?.big ?? image

  const { translate } = useTranslate()
  const {
    initialData: { allPromotions, catalogs },
  } = useInitialData()

  const isCampaignValidFlag = isCampaignValid(campaignValidityData)

  const campaign = getCustomAttrCustomFacetCampaign(customAttrArray)
  const label = getCustomAttrCustomFacetLabel(customAttrArray)

  const { promotionName: giftPromoName } = getFreeProductInfo(product)

  const { bundleType = '', bundlePrice } = product.priceInfo
  const bundleLabel = useBundleLabel(product.priceInfo, campaign, translate)

  const { promotionName: mainPromotion } = useMemo(
    () => getPromotionsTag(allPromotions)(promotionResults),
    [allPromotions, promotionResults]
  )

  const promotion = useMemo(
    () => campaign[0] || mainPromotion || bundleLabel || giftPromoName,
    [campaign, mainPromotion, bundleLabel, giftPromoName]
  )
  const isViewItem = useRef(false)
  const languageId = KK_LANG_IDS[locale as Locale]

  const { data: productInOffer } = useSWRImmutable<Product[]>(
    !isGloballyPricelist() &&
      mainPromotion &&
      `/api/products-in-offer/${manufacturerName.toLowerCase()}/${mainPromotion}`,
    getFetcher('products-in-offer', {
      languageId,
      manufacturerName: manufacturerName.toLowerCase(),
      promotion: mainPromotion,
      manufacturerId: product.manufacturerId,
    })
  )
  useEffect(() => {
    if (product && currency && !isViewItem.current) {
      const user = getCustomerUserData(customer)
      viewItem(product, currency, translate(promotion), user)
      isViewItem.current = true
    }
  }, [product, currency, customer])
  const priceInfo = usePriceInfo(product, allPromotions)
  const { normalPrice, topPrice } = priceInfo
  const isActiveCampaign = isGloballyPricelist()
    ? (normalPrice > topPrice || !!bundlePrice) && campaign && !!campaign.length
    : campaign && !!campaign.length

  const headingNode = (
    <ProductViewHeading
      manufacturerName={manufacturerName}
      productCode={sku}
      rating={averageRating}
      hasReviews={!!localReviews}
      name={getProductTitle(product)}
      campaign={isActiveCampaign ? campaign : undefined}
      giftPromoName={giftPromoName}
      webLabel={label}
      bundleLabel={bundleLabel}
      bundleType={bundleType}
      mainPromotion={mainPromotion}
    />
    // </SideLineWrap>
  )

  const parametersNode = (
    <ProductViewParameters product={product} activeCampaign={isCampaignValidFlag && Boolean(campaign)} />
  )
  const adaptiveHrNode = <CwsDivider variant="solid" />

  return (
    <CwsGridRow>
      <CwsGridCol col="12" xs="12" sm="6" lg="8">
        <div className={moduleStyles['left-column']}>
          {headingNode}
          <div className={moduleStyles.preview}>
            <div className="flex flex-1 relative w-full h-full">
              {currentImage && (
                <ZoomImage src={getFullImageUrl(currentImage)}>
                  <Image
                    alt={sku}
                    priority
                    fill
                    loading={'eager'}
                    sizes={'100vw'}
                    src={getFullImageUrl(currentImage)}
                    quality={80}
                    style={{ objectFit: 'contain' }}
                    className={'flex justify-center aspect-[5/3]'}
                  />
                </ZoomImage>
              )}
            </div>
          </div>

          <ExtraImagesSlider
            images={extraImages}
            selectedImageKey={currentImageIndex}
            onSelectImage={handleSelectImage}
            onClickNext={handleClickNextImage}
            onClickPrev={handleClickPrevImage}
          />

          {deviceSize === device360 ? (
            <>
              <CwsTab mode="fit-content" activeTabNavItemId="product_detail_description">
                {isDescriptionAvailable && (
                  <CwsTabItem id="product_detail_description" label={translate('product.detail.description.detail')} />
                )}
                <CwsTabItem id="product_description_reviews" label={translate('product.detail.description.reviews')} />
              </CwsTab>
              <CwsTabContent>
                {isDescriptionAvailable && (
                  <CwsTabContentItem id="product_detail_description">
                    <ProductViewDescription text={description} html={descriptionJSON} />
                  </CwsTabContentItem>
                )}

                <CwsTabContentItem id="product_description_reviews">
                  <ProductViewReviews
                    setAverageRating={setAverageRating}
                    reviewsTotal={localReviews}
                    setReviewsTotal={setLocalReviews}
                    sku={sku}
                    reviewsResult={reviews}
                    product={product}
                    inTab
                  />
                </CwsTabContentItem>
              </CwsTabContent>
            </>
          ) : (
            <>
              {isDescriptionAvailable && <ProductViewDescription text={description} html={descriptionJSON} />}

              {!isGloballyPricelist() && (
                <>
                  <ProductViewDeliveryInfo />
                  {adaptiveHrNode}
                </>
              )}
              <ProductViewReviews
                setAverageRating={setAverageRating}
                reviewsTotal={localReviews}
                setReviewsTotal={setLocalReviews}
                sku={sku}
                reviewsResult={reviews}
                product={product}
              />
            </>
          )}
        </div>
      </CwsGridCol>
      <CwsGridCol col="12" className={clsx(isGWPProduct && 'invisible')} xs="12" sm="6" lg="4">
        <StickyBox offsetTop={140} offsetBottom={0}>
          {parametersNode}
        </StickyBox>
      </CwsGridCol>
      <CwsGridCol col="12">
        {
          <>
            {productInOffer && productInOffer?.length > 0 && (
              <>
                {adaptiveHrNode}
                <div className="flex flex-col space-y-10 my-8">
                  <CwsH2 textAlign="center">{translate('collection.name.s2.in.offer')}</CwsH2>
                  <ProductSlider listName="recently_products" products={productInOffer} />
                  <div className="flex flex-row ">{/* <ProductCard></ProductCard> */}</div>
                </div>
              </>
            )}

            {recommendations?.productArray?.length > 0 && (
              <>
                {adaptiveHrNode}
                <div className="flex flex-col space-y-10 my-8">
                  <CwsH2 textAlign="center">{translate('collection.name.s2.recommendations')}</CwsH2>
                  <ProductSlider listName="recommended_products" products={recommendations.productArray} />
                </div>
              </>
            )}

            {recentlyViewedProducts?.length > 0 && (
              <>
                {adaptiveHrNode}
                <div className="flex flex-col space-y-10 my-8">
                  <CwsH2 textAlign="center">{translate('collection.name.s2.recent')}</CwsH2>
                  <ProductSlider listName="recently_products" products={recentlyViewedProducts} />
                  <div className="flex flex-row ">{/* <ProductCard></ProductCard> */}</div>
                </div>
              </>
            )}
          </>
        }
      </CwsGridCol>
    </CwsGridRow>
  )
}

export default ProductView

interface KeyValue {
  key: string | number
  value: string
}

interface ExtraImagesSliderProps {
  images: KeyValue[]
  selectedImageKey: KeyValue['key']
  onSelectImage?: (image: KeyValue) => any
  onClickNext: () => void
  onClickPrev: () => void
}

const ExtraImagesSlider: FC<ExtraImagesSliderProps> = (props) => {
  const { images: imagesSource, selectedImageKey, onSelectImage } = props
  const { deviceSize } = useResize()
  const totalImages = imagesSource?.length
  const imagesSortedByKey = imagesSource.sort((a, b) => parseInt(a.key.toString()) - parseInt(b.key.toString()))

  const selectedImageIndex = imagesSortedByKey.map((i) => i.key).indexOf(selectedImageKey) ?? 0
  const isSelectedLast = imagesSortedByKey[totalImages - 1]?.key === selectedImageKey
  const isSelectedFirst = imagesSortedByKey[0]?.key === selectedImageKey

  const { pagesToRender, leftOverflow, rightOverflow, ...otherpagelogic } = usePaginationLogic(
    totalImages,
    selectedImageIndex + 1,
    5
  )

  const images = useMemo(() => {
    return pagesToRender.map((index) => imagesSortedByKey[index - 1])
    // .filter(img => !!img)
  }, [pagesToRender, imagesSortedByKey])

  return (
    <CwsSection className="device360:mb-6">
      <CwsGrid width="fluid">
        <CwsGridRow noGutter>
          <CwsGridCol col="12" className={clsx(' flex items-center')}>
            <div
              className={clsx(
                moduleStyles.imagesScrollBar,
                'flex flex-row items-center gap-2 w-full device360:overflow-x-scroll'
              )}
            >
              {images.length > 1 &&
                images.map((image) => {
                  const { key, value } = image
                  if (!value) return null
                  return (
                    <div
                      key={key}
                      onClick={onSelectImage?.bind(this, image)}
                      className={clsx(
                        'border border-medium-grey cursor-pointer flex items-center content-center object-cover transition-all relative',
                        moduleStyles.imageSize,
                        selectedImageKey === key ? 'bg-group-grey' : 'bg-white'
                      )}
                    >
                      <div className={clsx(selectedImageKey === key && moduleStyles.selectedSliderImage)}>
                        <Image src={getFullImageUrl(value)} alt="" layout="fill" className="object-contain" />
                      </div>
                    </div>
                  )
                })}
            </div>
            {deviceSize >= device1024 && images.length > 1 && (
              <div className={clsx('flex flex-row space-x-4 ml-6', moduleStyles.arrows)}>
                <SliderButton
                  direction="left"
                  disabled={isSelectedFirst}
                  onClick={onSelectImage?.bind(this, imagesSortedByKey[selectedImageIndex - 1])}
                />
                <SliderButton
                  direction="right"
                  disabled={isSelectedLast}
                  onClick={onSelectImage?.bind(this, imagesSortedByKey[selectedImageIndex + 1])}
                />
              </div>
            )}
          </CwsGridCol>
        </CwsGridRow>
      </CwsGrid>
    </CwsSection>
  )
}
